import * as React from 'react';
import { useRequestFormSubmit } from '../../hooks/useRequestFormSubmit';
import getVisibleFields from '../../utils/getVisibleFields';
import FormButtonGroup from '../../components/FormButtonGroup';
import { useFormLabels } from '../../hooks/useFormLabels';
import { IRequestType } from '../../constants/interfaces';
import { fieldTypes } from '../../constants/app';
import { useRequestFormInput } from '../../hooks/useRequestFormInput';
import { usePropertyAddressOptions } from '../../hooks/usePropertyAddressOptions';

// Import modular components and hooks
import { useFormState } from '../../hooks/useFormState';
import { useFileUpload } from '../../hooks/useFileUpload';
import { useModalValidation } from '../../hooks/useModalValidation';
import { useAttachmentStatus } from '../../hooks/useAttachmentStatus';
import { FormLayout } from '../../components/FormLayout';
import { fieldValidations } from '../../utils/validation';
import { FileUploadSection } from '../../components/FileUploadSection';
import { formUi } from '../../constants/globalcss';
import {
  ValidationModals,
  ISnackbarState,
} from '../../components/ValidationModals';
import {
  Backdrop,
  CircularProgress,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import {
  headerContainerStyles,
  backButtonStyles,
  titleStyles,
} from '../WorkflowResponseForm/WorkflowResponseForm.styles';
import { ViewToggle } from '../HomePage/HelperComponents';

export enum AttachStatus {
  NotAllowed = -1,
  Optional = 0,
  Required = 1,
}

/**
 * Props for the RequestForm component
 */
export interface IAddRequestFormProps {
  /** Request type configuration */
  requestType: IRequestType;
  /** Whether to use grid layout (optional) */
  useGrid?: boolean;
}

/**
 * RequestForm component - Main form component for handling request submissions
 *
 * This component orchestrates form rendering, validation, file uploads, and modal handling
 * using modular hooks and components for better maintainability and performance.
 *
 * @param props - Component props
 * @returns JSX element containing the complete request form
 */
export default function RequestForm(props: IAddRequestFormProps) {
  const { useGrid, requestType } = props;

  const [loading, setLoading] = React.useState(false);

  // Core form state management
  const { labels, formState, setFormState, setLabels } = useFormLabels(
    requestType.id,
    setLoading
  );
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const rules = React.useMemo(
    () => JSON.parse(requestType.rules) || {},
    [requestType.rules]
  );

  // Snackbar state for user notifications
  const [snackbar, setSnackbar] = React.useState<ISnackbarState>({
    message: '',
    open: false,
    severity: 'success',
  });

  // Calculate visible fields
  const visibleFields = React.useMemo(
    () => getVisibleFields(labels, formState),
    [labels, formState]
  );

  // Initialize modular hooks
  const { resetTrigger, clearFormData } = useFormState(
    formState,
    setFormState,
    visibleFields
  );
  const { handleInputChange: handleNormalInputChange } = useRequestFormInput(
    setFormState,
    visibleFields
  );
  const attachmentStatus = useAttachmentStatus(rules, formState);

  const { handleFieldBlur } = usePropertyAddressOptions({
    requestType,
    setFormState,
    setLabels,
    setSnackbar,
  });

  // Initialize file upload management
  const {
    selectedFiles,
    handleFileChange,
    handleRemoveFile,
    handleClearFiles,
    clearFiles,
  } = useFileUpload((error: string) => setUploadErrorText(error));

  // Initialize modal validation management
  const {
    confirmation,
    handleConfirm: baseHandleConfirm,
    handleCancel,
    handleInputChangeWithValidation,
    handleSubmitWithValidation,
  } = useModalValidation(
    formState,
    () => {
      clearFormData();
      clearFiles();
    },
    labels,
    setSnackbar
  );

  // Initialize form submission hook with enhanced clear function
  const {
    handleSubmit: handleNormalSubmit,
    uploadErrorText,
    setUploadErrorText,
  } = useRequestFormSubmit({
    attachmentStatus,
    clearFormData: () => {
      clearFormData();
      clearFiles();
    },
    formState,
    requestType,
    selectedFiles,
    setErrors,
    setLoading,
    setSelectedFiles: () => {
      /* Managed by useFileUpload hook */
    },
    setSnackbar,
    visibleFields,
  });

  // Initialize form submission logic
  const handleConfirm = React.useCallback(() => {
    baseHandleConfirm(handleNormalSubmit);
  }, [baseHandleConfirm, handleNormalSubmit]);

  // Create wrapped handleInputChange that bridges the type difference
  const wrappedHandleNormalInputChange = React.useCallback(
    (
      key: string | number,
      value: string | number | boolean,
      children: unknown
    ) => {
      // Convert key to number (as expected by useRequestFormInput)
      const numericKey = typeof key === 'string' ? parseInt(key, 10) : key;
      // Convert value to string (as expected by useRequestFormInput)
      const stringValue = String(value);
      // Convert children to array (as expected by useRequestFormInput)
      const arrayChildren = Array.isArray(children)
        ? (children as [])
        : ([] as []);

      handleNormalInputChange(numericKey, stringValue, arrayChildren);
    },
    [handleNormalInputChange]
  );

  // Create wrapped handleInputChange
  const handleInputChange = React.useCallback(
    (
      key: string | number,
      value: string | number | boolean,
      children: unknown = []
    ) => {
      handleInputChangeWithValidation(
        key,
        value,
        children,
        wrappedHandleNormalInputChange
      );
    },
    [handleInputChangeWithValidation, wrappedHandleNormalInputChange]
  );

  // Calculate layout configuration
  const hasTableField = React.useMemo(
    () => visibleFields.some((field) => field.type === fieldTypes.table),
    [visibleFields]
  );
  const shouldUseGrid = Boolean(useGrid && !hasTableField);

  // Create form submission handler
  const handleSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      handleSubmitWithValidation(
        rules,
        selectedFiles,
        handleNormalSubmit,
        visibleFields,
        fieldValidations,
        setErrors
      );
    },
    [
      handleSubmitWithValidation,
      rules,
      selectedFiles,
      handleNormalSubmit,
      visibleFields,
      setErrors,
    ]
  );

  // Snackbar close handler
  const handleSnackbarClose = React.useCallback(() => {
    setSnackbar((s) => ({ ...s, open: false }));
  }, []);

  return (
    <>
      <Backdrop
        open={loading}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="primary" />
      </Backdrop>
      {/* Header and toggle moved to RequestFormView */}
      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          height: '100%',
        }}
      >
        <FormLayout
          visibleFields={visibleFields}
          formState={formState}
          errors={errors}
          handleInputChange={handleInputChange}
          shouldUseGrid={shouldUseGrid}
          resetTrigger={resetTrigger}
          hasTableField={hasTableField}
          onFieldBlur={handleFieldBlur}
        />
        <FileUploadSection
          hasUploadSupport={!!requestType.folder_path}
          selectedFiles={selectedFiles}
          onFileChange={handleFileChange}
          onRemoveFile={handleRemoveFile}
          attachmentStatus={attachmentStatus}
          onClearFiles={handleClearFiles}
          uploadErrorText={uploadErrorText}
          formContentMaxWidth={
            shouldUseGrid
              ? formUi.spacing.formColumnGridMax
              : formUi.spacing.formColumnListMax
          }
        />
        {visibleFields.length > 0 && (
          <Box sx={{ mx: 'auto', mt: 2, width: '100%' }}>
            <FormButtonGroup
              onClear={() => {
                clearFormData();
                clearFiles();
              }}
              submitting={loading}
              disableSubmit={loading}
              sx={{ mt: '12px' }}
            />
          </Box>
        )}
      </form>
      <ValidationModals
        showModal={confirmation.showModal}
        modalMessage={confirmation.validationResponse.message}
        modalTitle={confirmation.validationResponse.title || 'Confirm'}
        canContinue={confirmation.validationResponse.canContinue ?? true}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        snackbar={snackbar}
        onSnackbarClose={handleSnackbarClose}
      />
    </>
  );
}
